import { apiClientFormData } from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import { AxiosError } from 'axios';
import { collectFeedbackContext } from '@utils/feedbackContext';
import { logError } from '@utils/errorLogging';
import { createError } from '@utils/errorUtils';

import type { SubmitFeedbackPayloadT } from './types';

const RATE_LIMIT_MESSAGE = 'FEEDBACK_RATE_LIMITED';

export const isRateLimitError = (error: unknown) =>
  error instanceof Error && error.message === RATE_LIMIT_MESSAGE;

export const submitProductFeedbackApi = async (
  payload: SubmitFeedbackPayloadT,
): Promise<V4ApiResponse<string>> => {
  const context = collectFeedbackContext();

  const formData = new FormData();
  formData.append('FeedbackType', payload.feedbackType);
  formData.append('Text', payload.text.trim());
  formData.append('CurrentUrl', context.currentUrl);
  formData.append('RumSessionId', context.rumSessionId);
  formData.append('AppVersion', context.appVersion);
  formData.append('AllowContact', String(payload.allowContact));

  if (payload.componentSource !== undefined) {
    formData.append('ComponentSource', payload.componentSource);
  }
  if (payload.componentSourceId !== undefined) {
    formData.append('ComponentSourceId', String(payload.componentSourceId));
  }
  if (payload.screenshot !== undefined) {
    formData.append('Screenshot', payload.screenshot);
  }
  context.apiCalls.forEach((call) => formData.append('ApiCalls', call));

  try {
    // apiClientFormData has no response interceptor, so the envelope is still wrapped in AxiosResponse.data.
    const response = await apiClientFormData.post<V4ApiResponse<string>>(
      'Support/SubmitFeedback',
      formData,
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 429) {
      throw new Error(RATE_LIMIT_MESSAGE);
    }

    logError('submitProductFeedback', error as Error);
    throw createError(error, 'Unable to submit feedback');
  }
};
